<script setup lang="ts">
import { RawLocation } from 'vue-router';
import { TVulnerability } from '@/_core/entities/vulnerability/models/types';
import VulnerabilitySeverity from '@/_core/entities/vulnerability/ui/VulnerabilitySeverity/VulnerabilitySeverity.vue';
import Dropdown, { type TDropdownItem } from '@/_core/shared/ui/Dropdown/Dropdown.vue';
import { computed, getCurrentInstance } from 'vue';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import { trackReportVulnGotoClicked } from '@/services/analytics';

const props = defineProps<{
  vulnerability: TVulnerability;
  to: RawLocation;
  isInFalsePositivesFolder: boolean;
}>();

const emit = defineEmits<{
  (e: 'mark-as-false-positive'): void;
  (e: 'remove-from-false-positives'): void;
}>();

const instance = getCurrentInstance();
const toast = useToast();

function copyVulnLink() {
  const router = instance?.proxy.$router;
  const { href } = router?.resolve(props.to) || {};

  if (!href) return;

  try {
    navigator.clipboard.writeText(window.location.origin + href);
    toast.success({ message: 'Link copied to clipboard' });
  } catch (error) {
    console.error(error);
    toast.error({ message: 'Failed to copy link' });
  }
}

const actions = computed<TDropdownItem[]>(() => {
  const items: TDropdownItem[] = [
    {
      label: 'Copy Link',
      iconName: 'link',
      action: copyVulnLink,
    },
  ];

  if (props.isInFalsePositivesFolder) {
    items.push({
      label: 'Remove from False Positives',
      iconName: 'link',
      action: () => emit('remove-from-false-positives'),
    });
  } else {
    items.push({
      label: 'Mark as False Positive',
      iconName: 'link',
      action: () => emit('mark-as-false-positive'),
    });
  }

  return items;
});
</script>

<template>
  <router-link
    :to="to"
    class="tw:p-4 tw:flex-1 tw:shrink-0 tw:basis-auto tw:flex tw:gap-4 tw:items-center tw:lg:gap-6 tw:typography-small tw:rounded-2xl"
    :class="'tw:bg-general! tw:lg:hover:bg-main-blue/10! tw:transition-colors tw:no-underline! tw:text-secondary!'"
    @click.native="trackReportVulnGotoClicked(undefined, undefined)"
  >
    <div class="tw:w-20 tw:shrink-0 tw:truncate">
      {{ vulnerability.id }}
    </div>

    <div class="tw:w-25 tw:shrink-0">
      <VulnerabilitySeverity :severity="vulnerability['vulnerability-category.severity']" />
    </div>

    <div class="tw:w-64 tw:grow tw:shrink-0 tw:basis-auto tw:truncate tw:text-main">
      {{ vulnerability['vulnerability-category.descriptionTitle'] }}
    </div>

    <div class="tw:min-w-24 tw:flex tw:gap-4 tw:lg:gap-6 tw:items-center tw:justify-end">
      <div class="tw:flex-1 tw:grow-0 tw:flex tw:gap-1 tw:items-center tw:typography-sidenotes">
        <div class="tw:flex-1" v-if="vulnerability.hasCodeBlocks">
          <div class="tw:py-1 tw:px-3 tw:text-main-blue tw:bg-blue-bg tw:rounded-[100px]">SAST</div>
        </div>
        <div class="tw:flex-1 tw:grow-0" v-if="vulnerability.evidence">
          <div class="tw:py-1 tw:px-3 tw:text-main-blue tw:bg-blue-bg tw:rounded-[100px]">DAST</div>
        </div>
      </div>

      <Dropdown :items="actions">
        <div
          class="tw:p-1 tw:flex-1 tw:grow-0 tw:shrink-0 tw:basis-6 tw:text-ghost tw:lg:hover:bg-ghost/20 tw:rounded-md tw:lg:hover:text-secondary tw:lg:active:text-main tw:transition-colors tw:cursor-pointer"
        >
          <div class="tw:px-2.5 tw:py-1.25 tw:flex tw:flex-col tw:gap-0.5">
            <div class="tw:size-0.75 tw:bg-current tw:rounded-full"></div>
            <div class="tw:size-0.75 tw:bg-current tw:rounded-full"></div>
            <div class="tw:size-0.75 tw:bg-current tw:rounded-full"></div>
          </div>
        </div>
      </Dropdown>
    </div>
  </router-link>
</template>
